import { Campaign, Collab, activeLinks, activeUnits, isDeliveryOnlyUgc } from '@foodfluence/shared';
import type { CollabTask } from '@foodfluence/shared';
import { getEffectiveEndDate, isShippingCampaignType, isInStoreCampaignType, isRetailCampaignType } from 'utils/collabUtils';
import { toDate } from '../../../utils/dateUtils';
import { getContentDueDateRaw, getGoLivePostingDeadline, formatTimeLeft } from './dateUtils';
import { requiresBrandReview } from 'utils/campaignUtils';
import { isLastContentSubmitted } from 'utils/deliverableUtils';
import { isUGC, isCampaignFull, needsExtensionRequest } from './collabUtils';

// ---------------------------------------------------------------------------
// Home Page — Tasks Tab filters
// ---------------------------------------------------------------------------

export const byUrgency = (a: CollabTask, b: CollabTask): number => {
  if (!a.dueDate && !b.dueDate) return 0;
  if (!a.dueDate) return 1;
  if (!b.dueDate) return -1;
  return a.dueDate.getTime() - b.dueDate.getTime();
};

export const getSubmitLivePostTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timezone?: string
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    if (collab.status !== 'pendingSubmission' && collab.status !== 'pendingRedemption' && collab.status !== 'collabRedeemed') continue;

    const campaign = campaignData[collab.id];
    if (!campaign) continue;

    const content = collab.content;

    const links = collab.deliverableLinks;

    const plus3h = collab.scheduledCollabDate
      ? new Date(toDate(collab.scheduledCollabDate).getTime() + 3 * 60 * 60 * 1000)
      : null;
    const now = new Date();
    const inSubmissionWithoutRedemption = (collab.status === 'pendingRedemption' || collab.status === 'collabRedeemed')
                                          && isInStoreCampaignType(campaign.campaignType)
                                          && plus3h !== null
                                          && now > plus3h

    let hasPendingSubmission = false

    if (links && Object.keys(links).length > 0) {
      const deliverableLinksPendingSubmission = activeLinks(links).some(link => link.status === 'pendingSubmission')
      const ugcLinksPendingSubmissionOrVerification = activeUnits(content).some(unit => unit.status === "pendingVerification" || unit.status === "pendingSubmission");
      hasPendingSubmission = deliverableLinksPendingSubmission && !ugcLinksPendingSubmissionOrVerification
    }
    else if (inSubmissionWithoutRedemption) {
      // if the user missed their in store redemption window, the task still shows up as submit content
      hasPendingSubmission = !requiresBrandReview(campaign, collab)
    }

    if (hasPendingSubmission) {
      // Drafts all in: a go-live collab still posts by go-live end; others show no date (as before).
      const dueDate = !isLastContentSubmitted(collab, requiresBrandReview(campaign, collab))
        ? getContentDueDateRaw(collab, campaign, timezone)
        : getGoLivePostingDeadline(collab, campaign, timezone);
      tasks.push({ collab, title: isUGC(collab) ? 'Submit UGC Content' : 'Submit Live Post', dueDate });
    }
  }
  return tasks;
};

export const getSubmitDraftTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timezone?: string
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    if (collab.status !== 'pendingSubmission' && collab.status !== 'pendingRedemption' && collab.status !== 'collabRedeemed') continue;

    const campaign = campaignData[collab.id];
    if (!campaign) continue;

    const content = collab.content

    if (!requiresBrandReview(campaign, collab)) continue;
    const links = collab.deliverableLinks;

    const plus3h = collab.scheduledCollabDate
      ? new Date(toDate(collab.scheduledCollabDate).getTime() + 3 * 60 * 60 * 1000)
      : null;
    const now = new Date();
    const inSubmissionWithoutRedemption = (collab.status === 'pendingRedemption' || collab.status === 'collabRedeemed')
                                          && isInStoreCampaignType(campaign.campaignType)
                                          && plus3h !== null
                                          && now > plus3h

    let hasPendingDraft = false

    if (links && Object.keys(links).length > 0) {
      const deliverableLinksPendingSubmission = activeLinks(links).some(link => link.status === 'pendingDraftSubmission')
      const ugcLinksPendingVerification = activeUnits(content).some(unit => unit.status === "pendingVerification");
      hasPendingDraft = deliverableLinksPendingSubmission && !ugcLinksPendingVerification
    } 
    else if (inSubmissionWithoutRedemption) {
      // if the user missed their in store redemption window, the task still shows up as submit content
      hasPendingDraft = requiresBrandReview(campaign, collab)
    }

    if (hasPendingDraft) {
      tasks.push({
        collab,
        title: 'Submit Draft',
        dueDate: getContentDueDateRaw(collab, campaign, timezone),
      });
    }
  }
  return tasks;
};

export const getSubmitUGCContentTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timezone?: string
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    if (collab.status !== 'pendingSubmission') continue;
    const content = collab.content;
    const campaign = campaignData[collab.id];
    if (!campaign) continue;
    const hasPendingUGCSubmission = activeUnits(content).some(unit => unit.status === "pendingSubmission");
    if (hasPendingUGCSubmission) {
      tasks.push({
        collab,
        title: 'Submit UGC Content',
        dueDate: getContentDueDateRaw(collab, campaign, timezone),
      });
    }
  }
  return tasks;
};

export const getResubmitContentTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timezone?: string
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    if (collab.status !== 'pendingResubmission') continue;
    const campaign = campaignData[collab.id];
    if (!campaign) continue;
    tasks.push({
      collab,
      title: 'Resubmit Content',
      dueDate: getContentDueDateRaw(collab, campaign, timezone),
    });
  }
  return tasks;
};

export const getNeedToScheduleTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    if (collab.status !== 'pendingRedemption') continue;
    const campaign = campaignData[collab.id];
    if (!campaign) continue;
    const inStoreNeedsSchedule = isInStoreCampaignType(campaign.campaignType) && !collab.scheduledCollabDate;
    const retailNeedsSchedule = isRetailCampaignType(campaign.campaignType) && !collab.scheduledPostingDate;
    if (!inStoreNeedsSchedule && !retailNeedsSchedule) continue;
    const endDate = getEffectiveEndDate(campaign, collab);
    const title = inStoreNeedsSchedule
      ? 'Schedule Store Visit'
      : isDeliveryOnlyUgc(collab, campaign) ? 'Schedule Delivery Date' : 'Schedule Posting Date';
    tasks.push({
      collab,
      title,
      dueDate: endDate ? toDate(endDate) : null,
    });
  }
  return tasks;
};

export const getNeedToInputShippingAddressTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    if (collab.status !== 'pendingRedemption') continue;
    const campaign = campaignData[collab.id];
    if (!campaign) continue;
    if (!isShippingCampaignType(campaign.campaignType)) continue;
    const endDate = getEffectiveEndDate(campaign, collab);
    tasks.push({
      collab,
      title: 'Add Shipping Address',
      dueDate: endDate ? toDate(endDate) : null,
    });
  }
  return tasks;
};

export const getRedemptionTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timezone?: string
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    const campaign = campaignData[collab.id];
    if (!campaign) continue;

    if (collab.status === 'pendingShipment' && isShippingCampaignType(campaign.campaignType)) {
      tasks.push({
        collab,
        title: 'Confirm Package Delivery',
        dueDate: getContentDueDateRaw(collab, campaign, timezone),
      });
      continue;
    }

    if (
      collab.status === 'pendingRedemption' &&
      isRetailCampaignType(campaign.campaignType) &&
      collab.scheduledPostingDate
    ) {
      tasks.push({
        collab,
        title: 'Purchase Product',
        dueDate: getContentDueDateRaw(collab, campaign, timezone),
      });
      continue;
    }

    if (
      collab.status === 'pendingRedemption' &&
      isInStoreCampaignType(campaign.campaignType) &&
      collab.scheduledCollabDate
    ) {
      const visitDate = toDate(collab.scheduledCollabDate);
      if (!visitDate) continue;
      const now = new Date();
      const minus3h = new Date(visitDate.getTime() - 3 * 60 * 60 * 1000);
      const plus3h = new Date(visitDate.getTime() + 3 * 60 * 60 * 1000);

      let ctaLabel: string;
      let dueDate: Date | null;
      if (now >= minus3h && now <= plus3h) {
        ctaLabel = 'Visit Store & Redeem Now!';
        dueDate = plus3h;
      } else if (now < minus3h) {
        ctaLabel = 'Prepare to Visit Store';
        dueDate = minus3h;
      } else {
        // user has passed redemption window, allow them to submit their content
        continue;
      }

      tasks.push({ collab, title: ctaLabel, dueDate });
      continue;
    }
  }
  return tasks;
};

export const getViewRewardTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>
): CollabTask[] => {
  const tasks: CollabTask[] = [];
  for (const collab of collabs) {
    if (collab.status !== 'collabRedeemed') continue;
    const campaign = campaignData[collab.id];
    if (!campaign) continue;
    const visitDate = toDate(collab.scheduledCollabDate);
    if (!visitDate) continue;
    const plus3h = new Date(visitDate.getTime() + 3 * 60 * 60 * 1000);
    if (new Date() > plus3h) continue; // past window — handled by submit content tasks
    tasks.push({ collab, title: 'View Reward', dueDate: plus3h });
  }
  return tasks;
};

export const getDueSoonTasks = (tasks: CollabTask[]): CollabTask[] => {
  const threeDaysFromNow = Date.now() + 3 * 24 * 60 * 60 * 1000;
  return tasks.filter(t => t.dueDate != null && t.dueDate.getTime() <= threeDaysFromNow).sort(byUrgency);
};

export type TasksByCategory = {
  contentSubmission: CollabTask[];
  scheduling: CollabTask[];
  redemption: CollabTask[];
  rewards: CollabTask[];
};

/** `timezone`: the creator's due-date zone, for the ship-to-home fallback (getContentDueDateRaw). */
export const getTasksByCategory = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timezone?: string
): TasksByCategory => ({
  contentSubmission: [
    ...getSubmitLivePostTasks(collabs, campaignData, timezone),
    ...getSubmitDraftTasks(collabs, campaignData, timezone),
    ...getSubmitUGCContentTasks(collabs, campaignData, timezone),
    ...getResubmitContentTasks(collabs, campaignData, timezone),
  ].sort(byUrgency),
  scheduling: [
    ...getNeedToScheduleTasks(collabs, campaignData),
    ...getNeedToInputShippingAddressTasks(collabs, campaignData),
  ].sort(byUrgency),
  redemption: getRedemptionTasks(collabs, campaignData, timezone).sort(byUrgency),
  rewards: getViewRewardTasks(collabs, campaignData).sort(byUrgency),
});

export const getAllTasks = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>
): CollabTask[] => {
  const { contentSubmission, scheduling, redemption, rewards } = getTasksByCategory(collabs, campaignData);
  return [...contentSubmission, ...scheduling, ...redemption, ...rewards].sort(byUrgency);
};

export const getTasksTabSections = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  spotsMap: Record<string, number>,
  timezone?: string
): {
  dueSoon: CollabTask[];
  contentSubmission: CollabTask[];
  scheduling: CollabTask[];
  redemption: CollabTask[];
  rewards: CollabTask[];
} => {
  const shouldHide = (task: CollabTask): boolean => {
    const campaign = campaignData[task.collab.id];
    if (!campaign) return false;
    const spots = task.collab.influencerCampaignID
      ? spotsMap[task.collab.influencerCampaignID] ?? null
      : null;
    return (spots !== null && isCampaignFull(campaign, spots, task.collab)) || needsExtensionRequest(campaign, task.collab);
  };

  const categories = getTasksByCategory(collabs, campaignData, timezone);
  const allTasks = [
    ...categories.contentSubmission,
    ...categories.scheduling,
    ...categories.redemption,
    ...categories.rewards,
  ].sort(byUrgency).filter(t => !shouldHide(t));

  const dueSoon = getDueSoonTasks(allTasks);
  const dueSoonKeys = new Set(dueSoon.map(t => `${t.collab.id}::${t.title}`));
  const notDueSoon = (t: CollabTask) => !dueSoonKeys.has(`${t.collab.id}::${t.title}`);

  return {
    dueSoon,
    contentSubmission: categories.contentSubmission.filter(t => notDueSoon(t) && !shouldHide(t)),
    scheduling: categories.scheduling.filter(t => notDueSoon(t) && !shouldHide(t)),
    redemption: categories.redemption.filter(t => notDueSoon(t) && !shouldHide(t)),
    rewards: categories.rewards.filter(t => notDueSoon(t) && !shouldHide(t)),
  };
};

// ---------------------------------------------------------------------------
// Home Page — Pending Tab filters (mutually exclusive, priority ordered)
// ---------------------------------------------------------------------------

export { formatTimeLeft };
